/**
 * Arrival day kiosk — self-service, one scan, no new QR.
 *
 * The student presents the student ID QR they already have. The kiosk asks
 * Finance, Admissions, Housing and the Registry what they say, records only
 * the four self-service items, and sends the student to a desk ONLY where
 * something is genuinely outstanding.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  QrCode, Camera, CameraOff, CheckCircle2, AlertTriangle,
  Ticket, MapPin, Clock, BedDouble, RotateCcw, Sparkles,
} from 'lucide-react';

const SELF_LABEL = {
  orientation: 'I have attended orientation',
  welcome_pack: 'I have collected my welcome pack',
  library: 'Activate my library account',
  dining: 'Activate my dining plan',
};

const STATUS_TONE = {
  cleared: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-200',
  pending: 'border-amber-400/50 bg-amber-400/10 text-amber-100',
  not_required: 'border-white/10 bg-white/5 text-slate-400',
};

const Row = ({ item }) => (
  <div className={`flex items-start justify-between gap-4 rounded-sm border px-4 py-3 ${
    STATUS_TONE[item.state] || STATUS_TONE.pending}`}
       data-testid={`arrival-item-${item.key}`}>
    <div className="min-w-0">
      <p className="text-base font-medium">{item.label}</p>
      <p className="text-sm opacity-70 truncate">{item.message}</p>
    </div>
    <span className="text-xs uppercase tracking-widest whitespace-nowrap pt-1"
          data-testid={`arrival-item-status-${item.key}`}>
      {item.state === 'cleared' ? 'cleared'
        : item.state === 'not_required' ? 'n/a' : 'action needed'}
    </span>
  </div>
);

export default function ArrivalKiosk() {
  const [params] = useSearchParams();
  const [context, setContext] = useState(null);
  const [tenantId, setTenantId] = useState(params.get('tenant') || '');
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [camOn, setCamOn] = useState(false);
  const scannerRef = useRef(null);
  const resetRef = useRef(null);

  useEffect(() => {
    api.get('/school/arrival/kiosk/context')
      .then(({ data }) => {
        setContext(data);
        if (data.pinned_tenant_id) setTenantId(data.pinned_tenant_id);
      })
      .catch((e) => toast.error(getErrorMessage(e, 'This kiosk is not authorised')));
  }, []);

  const reset = useCallback(() => {
    if (resetRef.current) clearTimeout(resetRef.current);
    setState(null);
  }, []);

  const submit = useCallback(async (payload) => {
    if (!tenantId) { toast.error('No school is set for this kiosk'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/school/arrival/kiosk/scan',
        { tenant_id: tenantId, device: 'arrival-kiosk', ...payload });
      setState(data);
      if (resetRef.current) clearTimeout(resetRef.current);
      resetRef.current = setTimeout(() => setState(null), 90000);
    } catch (e) {
      toast.error(getErrorMessage(e, 'We could not read that'));
    } finally {
      setBusy(false);
    }
  }, [tenantId]);

  const stopCam = useCallback(async () => {
    try { await scannerRef.current?.stop(); } catch { /* already stopped */ }
    scannerRef.current = null;
    setCamOn(false);
  }, []);

  const startCam = useCallback(async () => {
    try {
      const scanner = new Html5Qrcode('arrival-kiosk-reader');
      scannerRef.current = scanner;
      await scanner.start({ facingMode: 'environment' }, { fps: 10, qrbox: 260 },
        async (text) => { await stopCam(); submit({ qr: text }); }, () => {});
      setCamOn(true);
    } catch (e) {
      toast.error('No camera available — type your student number instead');
      setCamOn(false);
    }
  }, [stopCam, submit]);

  useEffect(() => () => { scannerRef.current?.stop().catch(() => {}); }, []);

  const selfClear = async (key) => {
    setBusy(true);
    try {
      const { data } = await api.post('/school/arrival/kiosk/self-clear', {
        tenant_id: tenantId, student_id: state.student_id, steps: [key],
        visit_e: state.visit_e, visit_s: state.visit_s,
      });
      setState((prev) => ({ ...prev, ...data }));
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not record that'));
    } finally { setBusy(false); }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await api.post('/school/arrival/kiosk/finish', {
        tenant_id: tenantId, student_id: state.student_id,
        visit_e: state.visit_e, visit_s: state.visit_s,
      });
      toast.success('Welcome to college');
      reset();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Not quite finished yet'));
    } finally { setBusy(false); }
  };

  const school = context?.school?.name || 'Father Wunani Bible College';

  // ── Idle: scan me ───────────────────────────────────────────────
  if (!state) {
    return (
      <div className="min-h-screen bg-[#0B1020] text-white px-6 py-10 md:px-16"
           data-testid="arrival-kiosk-idle">
        <p className="text-xs uppercase tracking-[0.35em] text-[#C6971F]">{school}</p>
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] max-w-3xl"
            style={{ fontFamily: 'Newsreader, serif' }}>
          Welcome. Let's get you checked in.
        </h1>
        <p className="mt-4 text-base text-slate-400 max-w-xl">
          Hold your student ID card up to the camera. One scan is all we need —
          we will tell you if any desk needs to see you.
        </p>

        <div className="mt-12 grid gap-10 lg:grid-cols-[minmax(0,420px)_1fr] items-start">
          <div className="rounded-sm border border-white/10 bg-white/[0.03] p-6">
            <div id="arrival-kiosk-reader"
                 className={`overflow-hidden rounded-sm bg-black/50 ${camOn ? 'aspect-square' : 'h-0'}`} />
            {!camOn && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <QrCode className="h-20 w-20 text-[#C6971F]" />
                <p className="mt-4 text-sm text-slate-400">Camera is off</p>
              </div>
            )}
            <Button onClick={camOn ? stopCam : startCam}
                    data-testid="arrival-kiosk-camera-toggle"
                    className="mt-5 w-full rounded-sm bg-[#C6971F] hover:bg-[#A67816] text-black h-12 text-base">
              {camOn ? <><CameraOff className="h-5 w-5 mr-2" /> Stop the camera</>
                     : <><Camera className="h-5 w-5 mr-2" /> Scan my student ID</>}
            </Button>
          </div>

          <div className="rounded-sm border border-white/10 bg-white/[0.03] p-6 max-w-xl">
            <p className="text-sm uppercase tracking-widest text-slate-400">
              Lost your card?
            </p>
            <p className="mt-3 text-base text-slate-300" data-testid="arrival-kiosk-no-card">
              Please see a member of staff at the Admissions Desk — they will look
              you up and check you in. For your safety the kiosk can only read a
              student ID card.
            </p>
            {!!context?.items?.length && (
              <div className="mt-8">
                <p className="text-sm text-slate-400">What we check</p>
                <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-300">
                  {context.items.map((i) => (
                    <li key={i.key} className="flex items-center gap-2">
                      <span className="h-1 w-1 rounded-full bg-[#C6971F]" /> {i.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Result ──────────────────────────────────────────────────────
  const selfPending = (state.self_service_pending || []);
  const clearedAll = state.cleared;

  return (
    <div className="min-h-screen bg-[#0B1020] text-white px-6 py-10 md:px-16"
         data-testid={clearedAll ? 'arrival-kiosk-welcome' : 'arrival-kiosk-routing'}>
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.35em] text-[#C6971F]">
            {clearedAll ? 'All clear' : 'A few things to sort out'}
          </p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-semibold leading-tight"
              style={{ fontFamily: 'Newsreader, serif' }}
              data-testid="arrival-student-name">
            {state.student.name}
          </h1>
          <p className="mt-1 text-sm text-slate-400" data-testid="arrival-student-number">
            {state.student.student_number}
          </p>
        </div>
        <Button variant="ghost" onClick={reset} data-testid="arrival-kiosk-reset"
                className="text-slate-300 hover:text-white hover:bg-white/10 rounded-sm">
          <RotateCcw className="h-4 w-4 mr-2" /> Start again
        </Button>
      </div>

      {clearedAll ? (
        <div className="mt-10 max-w-3xl" data-testid="arrival-welcome-card">
          <div className="rounded-sm border border-emerald-400/30 bg-emerald-400/10 p-8">
            <CheckCircle2 className="h-12 w-12 text-emerald-300" />
            <h2 className="mt-5 text-lg md:text-lg font-semibold">
              You are fully checked in. Nothing else to do — welcome to {school}.
            </h2>
            <div className="mt-6 grid sm:grid-cols-3 gap-4 text-sm">
              <div className="rounded-sm bg-black/20 px-4 py-3" data-testid="arrival-welcome-room">
                <BedDouble className="h-4 w-4 text-[#C6971F]" />
                <p className="mt-2 text-slate-400">Your room</p>
                <p className="text-base">
                  {[state.welcome.block, state.welcome.room && `Room ${state.welcome.room}`,
                    state.welcome.bed && `Bed ${state.welcome.bed}`].filter(Boolean).join(' · ')
                    || 'Non-residential'}
                </p>
              </div>
              <div className="rounded-sm bg-black/20 px-4 py-3" data-testid="arrival-welcome-time">
                <Clock className="h-4 w-4 text-[#C6971F]" />
                <p className="mt-2 text-slate-400">Orientation</p>
                <p className="text-base">{state.welcome.orientation_time || '—'}</p>
              </div>
              <div className="rounded-sm bg-black/20 px-4 py-3" data-testid="arrival-welcome-venue">
                <MapPin className="h-4 w-4 text-[#C6971F]" />
                <p className="mt-2 text-slate-400">Venue</p>
                <p className="text-base">{state.welcome.orientation_venue || '—'}</p>
              </div>
            </div>
            <Button onClick={finish} disabled={busy} data-testid="arrival-kiosk-finish"
                    className="mt-7 h-12 rounded-sm bg-[#C6971F] hover:bg-[#A67816] text-black px-8 text-base">
              <Sparkles className="h-5 w-5 mr-2" /> Done — I'm all set
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-10 grid gap-8 lg:grid-cols-2 items-start">
          <div>
            <p className="text-sm uppercase tracking-widest text-slate-400">
              Where to go next
            </p>
            <div className="mt-4 space-y-4" data-testid="arrival-desk-list">
              {(state.pending_desks || []).map((desk) => {
                const ticket = (state.tickets || []).find((t) => t.desk === desk.key);
                return (
                  <div key={desk.key}
                       className="rounded-sm border border-amber-400/40 bg-amber-400/[0.07] p-5"
                       data-testid={`arrival-desk-${desk.key}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-lg font-semibold">{desk.label}</p>
                        <p className="text-sm text-slate-300 flex items-center gap-1 mt-1">
                          <MapPin className="h-3.5 w-3.5" /> {desk.location}
                        </p>
                      </div>
                      {ticket && (
                        <div className="text-right" data-testid={`arrival-ticket-${desk.key}`}>
                          <p className="text-xs uppercase tracking-widest text-slate-400">
                            <Ticket className="h-3.5 w-3.5 inline mr-1" /> Queue
                          </p>
                          <p className="text-3xl font-bold text-[#C6971F]">{ticket.number}</p>
                        </div>
                      )}
                    </div>
                    <ul className="mt-3 text-sm text-amber-100/90 space-y-1">
                      {desk.items.map((r) => (
                        <li key={r} className="flex items-start gap-2">
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5" /> {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
              {!(state.pending_desks || []).length && (
                <p className="text-sm text-slate-400">
                  No desk needs to see you — just finish the steps on the right.
                </p>
              )}
            </div>

            {!!selfPending.length && (
              <div className="mt-8">
                <p className="text-sm uppercase tracking-widest text-slate-400">
                  You can do these right here
                </p>
                <div className="mt-4 space-y-3" data-testid="arrival-self-service">
                  {selfPending.map((key) => (
                    <Button key={key} onClick={() => selfClear(key)} disabled={busy}
                            data-testid={`arrival-self-clear-${key}`}
                            className="w-full justify-start h-12 rounded-sm bg-white/10 hover:bg-white/20 text-white text-base">
                      <CheckCircle2 className="h-5 w-5 mr-3 text-[#C6971F]" />
                      {SELF_LABEL[key] || key}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <p className="text-sm uppercase tracking-widest text-slate-400">
              Your arrival checklist
            </p>
            <div className="mt-4 space-y-2" data-testid="arrival-checklist">
              {(state.items || []).map((item) => <Row key={item.key} item={item} />)}
            </div>
            <p className="mt-4 text-xs text-slate-500">
              Finance, Admissions, Accommodation and ID verification are read from the
              offices that own them — the kiosk cannot clear them for you.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
